/**
 * Capacitor Native Bridge — initializes native plugins when running inside a Capacitor app.
 * Import this once in index.js. It's a no-op on web browsers.
 *
 * All Capacitor plugins are statically imported (installed in frontend/package.json)
 * because the native shell loads from a remote URL, not a local bundle.
 */
import { SplashScreen } from '@capacitor/splash-screen';
import { Keyboard } from '@capacitor/keyboard';
import { App } from '@capacitor/app';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Network } from '@capacitor/network';
import { PushNotifications } from '@capacitor/push-notifications';

const isNative = () => {
  return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
};

const getPlatform = () => {
  if (!window.Capacitor) return 'web';
  return window.Capacitor.getPlatform ? window.Capacitor.getPlatform() : 'web';
};

/**
 * Apply safe-area CSS class to <html> IMMEDIATELY.
 * Called at import time (not async) so CSS rules apply before first paint.
 */
function _applySafeAreaClass() {
  if (typeof window === 'undefined' || !window.Capacitor) return;
  const platform = window.Capacitor.getPlatform?.();
  if (platform === 'ios') {
    document.documentElement.classList.add('capacitor-ios');
  } else if (platform === 'android') {
    document.documentElement.classList.add('capacitor-android');
  }
}
_applySafeAreaClass(); // Run immediately on import — before any async code

/**
 * Initialize all native plugins. Safe to call on web — everything is guarded.
 */
export async function initNativeBridge() {
  if (!isNative()) return;

  const platform = getPlatform();
  console.log(`[Native] Platform: ${platform}`);

  // --- Tenant Isolation ---
  // On first launch (or version change), clear any stale WebView data
  // that might belong to a previously installed tenant app.
  // Each tenant app has its own bundle, but the WebView's localStorage
  // persists across app updates — if a user re-installs a different
  // tenant's app to the same device slot, stale data could linger.
  try {
    const appInfo = await App.getInfo();
    const launchKey = `__tenant_launch_${appInfo.id}_${appInfo.version}`;
    if (!localStorage.getItem(launchKey)) {
      // First launch of this app version — clear stale tenant caches
      localStorage.removeItem('tenant_slug');
      localStorage.removeItem('impersonate_tenant_slug');
      localStorage.setItem(launchKey, Date.now().toString());
      console.log(`[Native] First launch of ${appInfo.id} v${appInfo.version} — cleared stale tenant cache`);
    }
  } catch (e) { /* App plugin not available */ }

  // Ensure classes are set (belt-and-suspenders — _applySafeAreaClass may have already run)
  if (platform === 'ios') document.documentElement.classList.add('capacitor-ios');
  if (platform === 'android') document.documentElement.classList.add('capacitor-android');

  // --- Status Bar / Safe Area ---
  // @capacitor-community/safe-area (see capacitor.config.json "SafeArea"/"SystemBars"
  // blocks) replaces @capacitor/status-bar. On iOS, env(safe-area-inset-*) already
  // works correctly out of the box. On Android, that CSS variable is only populated
  // for actual display cutouts — NOT the general status bar — so it silently reads
  // 0px there without this plugin, no matter what overlay/CSS approach is used. The
  // plugin fixes this at the native layer (real inset on modern WebViews, a padding
  // fallback on older Chromium), so the same env(safe-area-inset-top) CSS in
  // index.css now works on both platforms. Status bar / nav bar style is set
  // declaratively via capacitor.config.json — no runtime call needed here.

  // --- Splash Screen ---
  try {
    window.__hideSplash = async () => {
      try {
        await SplashScreen.hide({ fadeOutDuration: 300 });
      } catch (_) { /* ok */ }
    };
  } catch (e) { /* plugin not available */ }

  // --- Keyboard ---
  try {
    if (platform === 'ios') {
      await Keyboard.setAccessoryBarVisible({ isVisible: false });
      await Keyboard.setScroll({ isDisabled: false });
    }
    await Keyboard.addListener('keyboardWillShow', (info) => {
      document.documentElement.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
      document.body.classList.add('keyboard-open');
    });
    await Keyboard.addListener('keyboardWillHide', () => {
      document.documentElement.style.setProperty('--keyboard-height', '0px');
      document.body.classList.remove('keyboard-open');
    });
  } catch (e) { /* plugin not available */ }

  // --- App (back button + state) ---
  try {
    await App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        App.minimizeApp();
      }
    });

    await App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        document.dispatchEvent(new CustomEvent('app-resumed'));
        // Clear notification badge when app comes to foreground
        PushNotifications.removeAllDeliveredNotifications().catch(() => {});
      }
    });
  } catch (e) { /* plugin not available */ }

  // --- Haptics ---
  try {
    window.__haptic = async (style = 'Light') => {
      try {
        await Haptics.impact({ style: ImpactStyle[style] || ImpactStyle.Light });
      } catch (_) { /* ok */ }
    };
    window.__hapticNotification = async (type = 'Success') => {
      try {
        await Haptics.notification({ type: NotificationType[type] || NotificationType.Success });
      } catch (_) { /* ok */ }
    };
  } catch (e) { /* plugin not available */ }

  // --- Push Notifications ---
  // Permission is NOT requested here — handled by NotificationOnboarding.
  // Only set up listeners + register if permission was already granted from a previous session.
  try {
    const permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive === 'granted') {
      // 1. Set up listeners FIRST (so we catch the token event)
      const { initPushListeners } = await import('./pushService');
      await initPushListeners((n) => {
        if (n.foreground) {
          document.dispatchEvent(new CustomEvent('push-notification', { detail: n }));
        }
      });
      // 2. THEN register (fires the 'registration' event)
      try {
        await PushNotifications.register();
      } catch (regErr) {
        console.error('[Native] Push registration failed (Firebase not configured?):', regErr.message || regErr);
      }
    }
  } catch (e) { /* plugin not available */ }

  // --- Network ---
  // Consumed by NetworkContext.jsx (single app-wide connectivity source of truth
  // + NoInternetScreen) — this dispatch is the only thing that class needs from here.
  try {
    await Network.addListener('networkStatusChange', (status) => {
      document.dispatchEvent(new CustomEvent('network-change', { detail: status }));
    });
  } catch (e) { /* plugin not available */ }
}

/**
 * Hook: call from App.js useEffect to hide splash + register FCM token
 */
export function onAppReady() {
  if (window.__hideSplash) window.__hideSplash();
}

/**
 * Trigger haptic feedback on button press
 */
export function hapticTap() {
  if (window.__haptic) window.__haptic('Light');
}

export { isNative, getPlatform };
